import * as SliderPrimitive from '@radix-ui/react-slider'
import { BigNumber } from 'bignumber.js'
import { useEffect, useMemo, useState } from 'react'
import { v7 as uuidv7 } from 'uuid'
import { parseUnits } from 'viem'
import { Button } from '@/components/orders/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { publicConfig } from '@/config/publicConfig'
import { ButtonAction } from '@/constants/buttons'
import {
  getLedgerBalancesSelfQueryKey,
  listWithdrawalsQueryKey,
  useCreateWithdrawal,
} from '@/hooks/v1'
import type { Asset } from '@/hooks/v1/types/Asset'
import type { Position } from '@/hooks/v1/types/Position'
import { queryClient } from '@/services/api'
import { USDC_DECIMALS, validateUsdcAmount } from '@/services/web3/depositContract'
import { parseWithdrawalDestination } from '@/services/web3/withdrawalDestination'
import { resolveUsdcWithdrawalLedgerAsset } from '@/services/web3/withdrawalNativeAsset'
import { getApiErrorMessage } from '@/utils/apiErrorUtils'
import { createNotification, NotificationStatus, NotificationType } from '@/utils/notificationUtils'

type WithdrawUsdcModalProps = {
  isOpen: boolean
  onClose: () => void
  assets: Array<Asset>
  balances: Array<Position>
}

type LedgerBalanceState = {
  available: string
  decimals: number
}

function formatAvailable(value: string, decimals: number) {
  const amount = new BigNumber(value)
  if (!amount.isFinite()) return '0'
  const precision = Math.min(Math.max(decimals, 0), 8)
  return amount.decimalPlaces(precision, BigNumber.ROUND_DOWN).toFixed()
}

export function WithdrawUsdcModal({ isOpen, onClose, assets, balances }: WithdrawUsdcModalProps) {
  const [toAddress, setToAddress] = useState('')
  const [quantity, setQuantity] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const ledgerBalance = useMemo<LedgerBalanceState | undefined>(() => {
    const usdAsset = resolveUsdcWithdrawalLedgerAsset(assets)
    if (!usdAsset) return undefined

    const available = balances
      .filter((position) => position.asset_id === usdAsset.id)
      .reduce((sum, position) => sum.plus(position.available || '0'), new BigNumber(0))

    if (!available.isFinite() || available.isNegative()) {
      return { available: '0', decimals: USDC_DECIMALS }
    }

    return {
      available: available.toFixed(),
      decimals: USDC_DECIMALS,
    }
  }, [assets, balances])

  useEffect(() => {
    if (!isOpen) {
      const resetTimer = window.setTimeout(() => {
        setToAddress('')
        setQuantity('')
        setFormError(null)
      }, 0)
      return () => window.clearTimeout(resetTimer)
    }
  }, [isOpen])

  const destination = parseWithdrawalDestination(toAddress)
  const availableBaseUnits = useMemo(() => {
    if (!ledgerBalance) return 0n
    try {
      return parseUnits(ledgerBalance.available, USDC_DECIMALS)
    } catch {
      return 0n
    }
  }, [ledgerBalance])
  const amountValidation = validateUsdcAmount(
    quantity,
    publicConfig.web3Deposit.minimumUsdc,
    availableBaseUnits,
  )
  const sliderValue =
    ledgerBalance && availableBaseUnits > 0n && amountValidation.valid
      ? Math.min(
          new BigNumber(quantity).dividedBy(ledgerBalance.available).times(100).toNumber(),
          100,
        )
      : 0

  const {
    mutate: submitWithdrawal,
    isPending,
    reset: resetWithdrawalMutation,
  } = useCreateWithdrawal({
    mutation: {
      onSuccess: () => {
        createNotification(
          'Withdraw USDC',
          'Withdrawal request submitted. It will stay in review until an admin approves it.',
          NotificationType.SIMPLE,
        )

        void queryClient.invalidateQueries({ queryKey: getLedgerBalancesSelfQueryKey() })
        void queryClient.invalidateQueries({ queryKey: listWithdrawalsQueryKey() })
        void queryClient.invalidateQueries({ queryKey: [{ url: '/v1/web3/withdrawals' }] })

        onClose()
      },
      onError: (error) => {
        const message = getApiErrorMessage(error, 'Unable to submit withdrawal request.')
        setFormError(message)
        createNotification(
          'Withdraw USDC',
          message,
          NotificationType.SIMPLE,
          NotificationStatus.ERROR,
        )
      },
    },
  })

  const canConfirm = Boolean(destination && ledgerBalance && amountValidation.valid) && !isPending

  function handleSliderChange([percentage = 0]: Array<number>) {
    if (!ledgerBalance || availableBaseUnits <= 0n || isPending) return

    // Round down to token precision so the slider never requests more than the available balance.
    const amount = new BigNumber(ledgerBalance.available)
      .times(percentage)
      .dividedBy(100)
      .decimalPlaces(USDC_DECIMALS, BigNumber.ROUND_DOWN)

    setQuantity(amount.toFixed())
    setFormError(null)
  }

  function handleClose() {
    if (isPending) return
    resetWithdrawalMutation()
    onClose()
  }

  function handleConfirm() {
    const parsedDestination = parseWithdrawalDestination(toAddress)
    if (!parsedDestination) {
      setFormError('Enter a valid Ethereum address. The zero address is not allowed.')
      return
    }

    if (!ledgerBalance) {
      setFormError('USD ledger balance is not available for withdrawals.')
      return
    }

    if (!amountValidation.valid) {
      if (amountValidation.reason === 'insufficient-usdc') {
        setFormError('Requested quantity exceeds available balance.')
      } else if (amountValidation.reason === 'below-minimum') {
        setFormError(`Minimum withdrawal is ${publicConfig.web3Deposit.minimumUsdc} USDC.`)
      } else {
        setFormError('Enter a positive USDC quantity with at most 6 decimal places.')
      }
      return
    }

    setFormError(null)
    submitWithdrawal({
      body: {
        withdrawal_id: uuidv7(),
        to_address: parsedDestination,
        quantity,
      },
    })
  }

  const labelClasses = 'mb-2 block text-xs uppercase tracking-wide text-modal-caption'
  const inputClasses =
    'h-12 border border-modal-input-border bg-modal-input-bg text-lg text-modal-input-text placeholder:text-modal-caption-dim focus-visible:ring-modal-focus'

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-[520px] rounded-2xl border !border-modal-border !bg-modal-surface text-modal-text shadow-[0_16px_36px_-20px_rgba(6,22,45,0.55)]">
        <DialogHeader className="pb-3">
          <DialogTitle className="font-semibold tracking-wide text-modal-text">
            Withdraw USDC
          </DialogTitle>
          <p className="text-xs text-modal-description">
            Request a USDC withdrawal to an Ethereum address. The amount is reserved immediately and
            stays in review until an admin approves it.
          </p>
        </DialogHeader>

        <div className="space-y-5 text-sm text-modal-copy">
          <div>
            <Label className={labelClasses}>Asset</Label>
            <Input value="USDC" readOnly disabled className={inputClasses} />
            {ledgerBalance ? (
              <div className="mt-1 space-y-0.5 text-xs">
                <p className="text-modal-caption">
                  Available to withdraw:{' '}
                  {formatAvailable(ledgerBalance.available, ledgerBalance.decimals)} USDC
                </p>
                <p className="text-modal-description">Uses your USD balance at 1 USD = 1 USDC.</p>
              </div>
            ) : (
              <p className="mt-1 text-xs text-red-400">
                Ledger balance is unavailable for this account.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="withdraw-usdc-destination" className={labelClasses}>
              Destination address
            </Label>
            <Input
              id="withdraw-usdc-destination"
              data-hotkeys-disabled
              value={toAddress}
              placeholder="0x…"
              onChange={(event) => {
                setToAddress(event.target.value)
                setFormError(null)
              }}
              disabled={isPending}
              className={inputClasses}
              autoComplete="off"
              spellCheck={false}
            />
            {toAddress && !destination ? (
              <p className="mt-1 text-xs text-red-400">
                Enter a valid 0x-prefixed address. The zero address is not allowed.
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="withdraw-usdc-quantity" className={labelClasses}>
              Quantity
            </Label>
            <Input
              id="withdraw-usdc-quantity"
              data-hotkeys-disabled
              type="text"
              inputMode="decimal"
              value={quantity}
              placeholder="0.00"
              onChange={(event) => {
                setQuantity(event.target.value)
                setFormError(null)
              }}
              disabled={!ledgerBalance || isPending}
              className={inputClasses}
            />
            <div className="mt-3">
              <SliderPrimitive.Root
                data-hotkeys-disabled
                value={[Number.isFinite(sliderValue) ? sliderValue : 0]}
                onValueChange={handleSliderChange}
                min={0}
                max={100}
                step={1}
                disabled={!ledgerBalance || availableBaseUnits <= 0n || isPending}
                className="relative flex h-5 w-full touch-none select-none items-center data-[disabled]:opacity-50"
              >
                <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-white/15">
                  <SliderPrimitive.Range className="absolute h-full bg-modal-focus" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb
                  aria-label="Percentage of available balance to withdraw"
                  aria-valuetext={`${Math.round(Number.isFinite(sliderValue) ? sliderValue : 0)}%`}
                  className="relative block size-[10px] rounded-full bg-modal-focus transition-all hover:size-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-modal-focus/60 disabled:pointer-events-none"
                />
              </SliderPrimitive.Root>
              <div className="mt-1 flex justify-between px-0.5" aria-hidden="true">
                {[0, 25, 50, 75, 100].map((tick) => (
                  <span key={tick} className="text-xs text-modal-caption">
                    {tick}%
                  </span>
                ))}
              </div>
            </div>
            {quantity &&
            amountValidation.valid === false &&
            amountValidation.reason === 'insufficient-usdc' ? (
              <p className="mt-1 text-xs text-red-400">
                Requested quantity exceeds available balance.
              </p>
            ) : null}
          </div>

          {formError ? <p className="text-xs text-red-400">{formError}</p> : null}

          <div className="flex flex-col gap-3 sm:flex-row justify-end">
            <Button
              type="button"
              action={ButtonAction.Primary}
              className="uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
              size="sm"
              variant="outline"
              noOpacityColors
              disabled={isPending}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              action={ButtonAction.Primary}
              className="uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]"
              size="sm"
              state={{ isLoading: isPending }}
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              Confirm Withdrawal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
